import { exhaustiveSwitchError } from "../client/upstream/control"

export type BinaryOp =
  | "+"
  | "-"
  | "*"
  | "/"
  | "^"
  | "%"
  | "&&"
  | "||"
  | "=="
  | "!="
export type UnaryOp =
  | "sin"
  | "cos"
  | "tan"
  | "abs"
  | "neg"
  | "log"
  | "sqrt"
  | "!"
  | "length"
  | "floor"

type Lit<T> = {
  type: "lit"
  value: T
}

type Ref<T> = {
  type: "ref"
  id: string
}

type BinaryExpr<T> = {
  type: "binary"
  op: BinaryOp
  left: Expr<T> | null
  right: Expr<T> | null
}

type UnaryExpr<T> = {
  type: "unary"
  op: UnaryOp
  operand: Expr<T> | null
}
export type Color = string & { __brand: "Color" }

export type Expr<T> = Lit<T> | Ref<T> | BinaryExpr<T> | UnaryExpr<T>

export type Env = Record<string, Expr<any>>

export namespace Expr {
  export const colors = {
    red: { type: "lit", value: "red" } as Lit<Color>,
    green: { type: "lit", value: "green" } as Lit<Color>,
    blue: { type: "lit", value: "blue" } as Lit<Color>,
    yellow: { type: "lit", value: "yellow" } as Lit<Color>,
    black: { type: "lit", value: "black" } as Lit<Color>,
  }

  export function lit<T>(value: T): Lit<T> {
    return { type: "lit", value }
  }

  export function ref<T>(id: string): Ref<T> {
    return { type: "ref", id }
  }

  // Main expression type

  export function evaluate<T>(expr: Expr<T>, env: Env): T {
    if (!expr) throw new Error("null expr")

    const envWithTime: Env = {
      ...env,
      time: Expr.lit(Date.now()),
    }

    switch (expr.type) {
      case "lit":
        return expr.value

      case "ref": {
        const refExpr = envWithTime[expr.id]
        if (!refExpr) throw new Error(`Parameter ${expr.id} not found`)
        return evaluate(refExpr, env)
      }

      case "binary": {
        if (!expr.left || !expr.right) throw new Error("Missing operand")
        const left = evaluate(expr.left, env)
        const right = evaluate(expr.right, env)

        switch (expr.op) {
          case "+":
            return (left as any) + (right as any)
          case "-":
            return ((left as any) - (right as any)) as any
          case "*":
            return ((left as any) * (right as any)) as any
          case "/":
            return ((left as any) / (right as any)) as any
          case "^":
            return Math.pow(left as number, right as number) as any
          case "%":
            return ((left as any) % (right as any)) as any
          case "&&":
            return (left as any) && (right as any)
          case "||":
            return (left as any) || (right as any)
          case "==":
            return ((left as any) === (right as any)) as any
          case "!=":
            return ((left as any) !== (right as any)) as any
          default:
            exhaustiveSwitchError(expr, "op")
        }
      }

      case "unary": {
        if (!expr.operand) throw new Error("Missing operand")
        const operand = evaluate(expr.operand, env)

        switch (expr.op) {
          case "neg":
            return -operand as any
          case "!":
            return !(operand as any) as any
          case "abs":
            return Math.abs(operand as any) as any
          case "log":
            return Math.log(operand as any) as any
          case "sqrt":
            return Math.sqrt(operand as any) as any
          case "length":
            return (operand as any).length
          case "sin":
            return Math.sin(operand as any) as any
          case "cos":
            return Math.cos(operand as any) as any
          case "tan":
            return Math.tan(operand as any) as any
          case "floor":
            return Math.floor(operand as any) as any

          default:
            exhaustiveSwitchError(expr, "op")
        }
      }

      default:
        exhaustiveSwitchError(expr, "type")
    }
  }
}
